import { type HTMLAttributes, forwardRef } from 'react'
import { cn } from '@/utils/cn'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'pink' | 'blue' | 'mint' | 'purple' | 'yellow' | 'red' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
  dotColor?: string
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      dot = false,
      dotColor,
      children,
      ...props
    },
    ref
  ) => {
    const variants = {
      default: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
      pink: 'bg-light-baby-pink/20 text-pink-700 dark:bg-dark-burnt-peach/20 dark:text-dark-burnt-peach',
      blue: 'bg-light-icy-blue/20 text-blue-700 dark:bg-light-icy-blue/20 dark:text-light-icy-blue',
      mint: 'bg-light-frosted-mint/20 text-green-700 dark:bg-dark-muted-teal/20 dark:text-dark-muted-teal',
      purple: 'bg-light-mauve/20 text-purple-700 dark:bg-light-mauve/20 dark:text-light-mauve',
      yellow: 'bg-light-lemon-chiffon/30 text-amber-700 dark:bg-dark-apricot-cream/20 dark:text-dark-apricot-cream',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      outline: 'bg-transparent border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300',
    }

    const sizes = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-0.5 text-xs',
      lg: 'px-3 py-1 text-sm',
    }

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 font-medium rounded-full',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: dotColor || 'currentColor' }}
          />
        )}
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

// Agent badges
export const AgentBadge = ({
  agent,
  size = 'md',
}: {
  agent: 'cleaner' | 'qai' | 'aai' | 'oai' | 'human'
  size?: 'sm' | 'md' | 'lg'
}) => {
  const config = {
    cleaner: { label: 'Cleaner AI', color: '#f0997b', variant: 'pink' as const },
    qai: { label: 'QAI', color: '#ed93b1', variant: 'pink' as const },
    aai: { label: 'AAI', color: '#5dcaa5', variant: 'mint' as const },
    oai: { label: 'OAI', color: '#afa9ec', variant: 'purple' as const },
    human: { label: 'You', color: '#97c459', variant: 'mint' as const },
  }

  const { label, color, variant } = config[agent]

  return (
    <Badge variant={variant} size={size} dot dotColor={color}>
      {label}
    </Badge>
  )
}

// Branch status badge
export const BranchStatusBadge = ({
  status,
  size = 'md',
}: {
  status: 'active' | 'terminated_natural' | 'terminated_manual' | 'pruned'
  size?: 'sm' | 'md' | 'lg'
}) => {
  const config = {
    active: { label: 'Active', variant: 'blue' as const },
    terminated_natural: { label: 'Completed', variant: 'mint' as const },
    terminated_manual: { label: 'Terminated', variant: 'yellow' as const },
    pruned: { label: 'Pruned', variant: 'default' as const },
  }

  const { label, variant } = config[status]

  return (
    <Badge variant={variant} size={size}>
      {label}
    </Badge>
  )
}

export default Badge
